/**
 * CalendarModeTabs — the page-level mode switch (S4):
 * Alle | Aufgaben | Rapporte | Geburtstage, backed by the `?mode=` URL
 * parameter.
 *
 * The modes are EXCLUSIVE except "all", which is the union of every source.
 * Birthdays joined that union rather than arriving as a separate checkbox in
 * CalendarFilters: those filters narrow tasks by assignee, status and type —
 * properties a birthday does not have — and a second, differently-shaped
 * filter concept beside this one is how a calendar ends up with two answers
 * to "what am I looking at". Because "all" is the default, birthdays are
 * visible without anyone opting in.
 *
 * URL contract:
 *   ?mode=all | tasks | reports | birthdays
 *   missing or invalid value → "all" (never crashes, never redirects)
 *   switching PUSHES a history entry (browser back/forward works) and
 *   preserves every other query parameter.
 *
 * Rendered with the shadcn Tabs primitives (real tablist/tab roles,
 * keyboard support, data-state for the active mode) using semantic
 * theme tokens — works in light and dark without extra logic.
 */
import React from "react";
import { useSearchParams } from "react-router-dom";

import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";

export const CALENDAR_MODES = ["all", "tasks", "reports", "birthdays"];

/** Sanitize a raw ?mode= value: unknown/missing → "all". */
export function normalizeCalendarMode(raw) {
  return CALENDAR_MODES.includes(raw) ? raw : "all";
}

/**
 * Read + write the calendar mode from the URL. Exposed as a hook so
 * AdminCalendarPage owns exactly one source of truth for the mode.
 */
export function useCalendarMode() {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = normalizeCalendarMode(searchParams.get("mode"));

  const setMode = React.useCallback(
    (next) => {
      const value = normalizeCalendarMode(next);
      setSearchParams(
        (prev) => {
          // Copy so every other existing query parameter survives.
          const params = new URLSearchParams(prev);
          if (value === "all") {
            params.delete("mode"); // canonical URL for the default mode
          } else {
            params.set("mode", value);
          }
          return params;
        }
        // default push semantics → browser back/forward steps through modes
      );
    },
    [setSearchParams]
  );

  return [mode, setMode];
}

const MODE_LABELS = [
  { value: "all", label: "Alle" },
  { value: "tasks", label: "Aufgaben" },
  { value: "reports", label: "Rapporte" },
  { value: "birthdays", label: "Geburtstage" },
];

export default function CalendarModeTabs({ mode, onModeChange }) {
  return (
    <Tabs value={mode} onValueChange={onModeChange}>
      <TabsList aria-label="Kalender-Modus">
        {MODE_LABELS.map((m) => (
          <TabsTrigger
            key={m.value}
            value={m.value}
            className="min-h-[36px] px-4"
          >
            {m.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
